import { useCallback, useEffect, useState } from "react";
import { Tag, Loader2, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const eur = (v) => `€ ${Number(v).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function PricingBlock() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/pricing");
      setData(d);
      setForm({ enabled: d.intro.enabled, STANDARD: d.intro.amounts.STANDARD, PREMIUM: d.intro.amounts.PREMIUM, ends_at: d.intro.ends_at || "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/admin/pricing", {
        intro_enabled: form.enabled,
        intro_amounts: { STANDARD: form.STANDARD, PREMIUM: form.PREMIUM },
        intro_ends_at: form.ends_at || null,
      });
      setData(d);
      setForm({ enabled: d.intro.enabled, STANDARD: d.intro.amounts.STANDARD, PREMIUM: d.intro.amounts.PREMIUM, ends_at: d.intro.ends_at || "" });
      toast.success(d.intro.active ? "Introductieprijs staat aan — nieuwe klanten betalen het eerste jaar minder." : "Introductieprijs staat uit — nieuwe klanten betalen de reguliere prijs.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const input = "w-full border-2 border-foreground bg-background px-3 py-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-primary";

  return (
    <section className="bg-card border-2 border-foreground heavy-shadow" data-testid="pricing-admin-block">
      <div className="p-5 border-b-2 border-foreground">
        <h2 className="font-display font-black text-2xl uppercase tracking-tight inline-flex items-center gap-2"><Tag className="w-5 h-5" /> Prijzen</h2>
        <p className="text-xs text-muted-foreground mt-1">De introductieprijs geldt voor het <b>eerste jaar</b> van nieuwe abonnementen. De verlenging (Mollie-abonnement) loopt altijd tegen de reguliere prijs. Kortingscodes worden berekend over de getoonde prijs voor het eerste jaar.</p>
      </div>
      {!data || !form ? <div className="p-8 text-center text-muted-foreground text-sm">Laden…</div> : (
        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3" data-testid="pricing-admin-current">
            {["STANDARD", "PREMIUM"].map((k) => {
              const p = data.plans[k];
              return (
                <div key={k} className="border-2 border-foreground/30 p-3" data-testid={`pricing-admin-plan-${k.toLowerCase()}`}>
                  <div className="stat-label">{p.name} · nu voor nieuwe klanten</div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-display font-black text-2xl" data-testid={`pricing-admin-first-${k.toLowerCase()}`}>{eur(p.first_year)}</span>
                    {p.intro_active && <span className="text-sm text-muted-foreground line-through">{eur(p.regular)}</span>}
                    <span className="text-xs text-muted-foreground">eerste jaar</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Verlenging: {eur(p.regular)} per jaar</div>
                </div>
              );
            })}
          </div>

          <div className="grid sm:grid-cols-[auto_1fr_1fr_1fr] gap-4 items-end">
            <button type="button" onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))} data-testid="pricing-admin-toggle" aria-pressed={form.enabled}
              className={`btn-brutal px-3 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-2 whitespace-nowrap ${form.enabled ? "bg-positive text-black" : "bg-background text-foreground"}`}>
              <span className={`inline-block w-8 h-4 border-2 border-foreground relative ${form.enabled ? "bg-foreground" : "bg-background"}`}>
                <span className={`absolute top-0 h-3 w-3 bg-background border border-foreground transition-transform ${form.enabled ? "translate-x-4" : "translate-x-0"}`} />
              </span>
              Introductieprijs: {form.enabled ? "aan" : "uit"}
            </button>
            <label className="block">
              <span className="stat-label">Standaard eerste jaar (€)</span>
              <input value={form.STANDARD} onChange={(e) => setForm((f) => ({ ...f, STANDARD: e.target.value }))} inputMode="decimal" data-testid="pricing-admin-standard" className={input} disabled={!form.enabled} />
              <span className="text-[11px] text-muted-foreground">regulier {eur(data.plans.STANDARD.regular)}</span>
            </label>
            <label className="block">
              <span className="stat-label">Premium eerste jaar (€)</span>
              <input value={form.PREMIUM} onChange={(e) => setForm((f) => ({ ...f, PREMIUM: e.target.value }))} inputMode="decimal" data-testid="pricing-admin-premium" className={input} disabled={!form.enabled} />
              <span className="text-[11px] text-muted-foreground">regulier {eur(data.plans.PREMIUM.regular)}</span>
            </label>
            <label className="block">
              <span className="stat-label">Geldig t/m (optioneel)</span>
              <input type="date" value={form.ends_at} onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))} data-testid="pricing-admin-ends-at" className={input} disabled={!form.enabled} />
              <span className="text-[11px] text-muted-foreground">leeg = tot je 'm uitzet</span>
            </label>
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] text-muted-foreground" data-testid="pricing-admin-meta">
              {data.updated_at ? <>Laatst gewijzigd {fmt(data.updated_at)}{data.updated_by ? ` door ${data.updated_by}` : ""}</> : "Standaardinstelling (nog niet gewijzigd)"}
              {data.intro.enabled && data.intro.ends_at && !data.intro.active && <span className="text-destructive"> · einddatum verstreken — reguliere prijs actief</span>}
            </div>
            <button onClick={save} disabled={saving} data-testid="pricing-admin-save" className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Opslaan
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
